/**
 * Build an RFC 5322 .eml message for offline drafting. Includes a brand metadata
 * header so any client (Apple Mail, Outlook, Thunderbird) can identify the origin
 * — fueling SaiResponse's viral discovery loop.
 */
import { Button } from "./ui/button";
import { Download } from "lucide-react";
import { toast } from "sonner";

function fold(line) {
  // RFC 5322 line length — split very long header values at ~76 chars.
  if (line.length <= 76) return line;
  const parts = [];
  let i = 0;
  while (i < line.length) {
    parts.push(line.slice(i, i + 72));
    i += 72;
  }
  return parts.join("\r\n  ");
}

function buildEml({ subject, output, fromName, fromEmail, personaLabel, modeLabel, structure }) {
  const now = new Date();
  const dateRfc = now.toUTCString().replace("GMT", "+0000");
  const messageId = `<sairesponse-${now.getTime()}.${Math.random().toString(36).slice(2, 9)}@sairesponse.com>`;
  const cleanSubject = (subject || "Draft from SaiResponse").replace(/[\r\n]+/g, " ").slice(0, 200);

  const headers = [
    "MIME-Version: 1.0",
    `Date: ${dateRfc}`,
    `Message-ID: ${messageId}`,
    `From: ${fold(`${fromName || "Me"} <${fromEmail || "me@example.com"}>`)}`,
    "To: ",
    `Subject: ${fold(cleanSubject)}`,
    "X-Mailer: SaiResponse 1.0 (https://sairesponse.com)",
    "X-Drafted-With: SaiResponse — Smart Automated Intelligence",
    `X-SaiResponse-Persona: ${fold(personaLabel || "")}`,
    `X-SaiResponse-Mode: ${fold(modeLabel || "")}`,
    `X-SaiResponse-Structure: ${structure || "raw_text"}`,
    "Content-Type: text/plain; charset=utf-8",
    "Content-Transfer-Encoding: 8bit",
    "",
  ];

  const body =
    output +
    "\r\n\r\n" +
    "-- \r\n" +
    "Drafted with SaiResponse · https://sairesponse.com\r\n";

  return headers.join("\r\n") + "\r\n" + body;
}

export default function EmlDownloadButton({ output, fromName, fromEmail, personaLabel, modeLabel, structure, disabled }) {
  const onClick = () => {
    if (!output) {
      toast.error("Generate a response first.");
      return;
    }
    // Try to infer a subject from the first non-empty line.
    const firstLine = (output.split(/\n/).find((l) => l.trim()) || "Draft from SaiResponse").trim();
    const subject = firstLine.length > 80 ? firstLine.slice(0, 77) + "…" : firstLine;
    const eml = buildEml({ subject, output, fromName, fromEmail, personaLabel, modeLabel, structure });
    const blob = new Blob([eml], { type: "message/rfc822" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sairesponse-draft-${Date.now()}.eml`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 500);
    toast.success("Draft saved (.eml)");
  };

  return (
    <Button
      data-testid="download-eml-btn"
      size="sm"
      variant="outline"
      onClick={onClick}
      disabled={disabled || !output}
      className="h-8 border-border bg-charcoal hover:bg-white/[0.04] text-white/80 rounded-sm"
    >
      <Download className="h-3.5 w-3.5 mr-1.5" /> Save .eml
    </Button>
  );
}
